import {type FC, useEffect, useState} from "react";
import {Description, Field, Label, Radio, RadioGroup} from "@headlessui/react";
import type {
  ICreditCancelEffective,
  ICreditSubscription,
  ICreditSubscriptionChangeResponse,
  ICreditsBalance,
} from "../../models/interfaces/credits.interface.ts";
import BillingActionConfirmation, {
  type BillingActionPhase,
  type BillingSummaryRow,
  POLAR_REFUND_NOTE,
  SIMULATED_NOTE,
} from "./BillingActionConfirmation.tsx";

// C5 unused-refund cents for a period nothing has been refunded from yet: floor(P * unused / C). This is
// the preview only; the refund in the response replaces it once the cancellation is confirmed.
const estimateUnusedRefundCents = (priceCents: number, planCredits: number, unusedCredits: number): number =>
  planCredits <= 0 ? 0 : Math.floor((priceCents * unusedCredits) / planCredits);

const DEFAULT_EFFECTIVE: ICreditCancelEffective = "period_end";

const utcFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const usdFormatter = new Intl.NumberFormat("en-US", {style: "currency", currency: "USD"});

const formatUtc = (iso: string): string => `${utcFormatter.format(new Date(iso))}\u00a0UTC`;
const formatUsdCents = (cents: number): string => usdFormatter.format(cents / 100);
const formatCount = (credits: number): string => credits.toLocaleString("en-US");
const formatCreditCount = (credits: number): string => `${formatCount(credits)} credits`;
const formatAmount = (cents: number, simulated: boolean): string =>
  simulated ? `${formatUsdCents(cents)} (simulated)` : formatUsdCents(cents);

interface CancelOption {
  value: ICreditCancelEffective;
  label: string;
  description: string;
}

interface DoneView {
  title: string;
  rows: BillingSummaryRow[];
}

interface CancelSubscriptionDialogProps {
  isOpen: boolean;
  subscriptionSnapshot: ICreditSubscription | null;
  planName: string;
  planRemainingCredits: number;
  nonPlanCredits: number;
  phase: BillingActionPhase;
  message: string | null;
  result: ICreditSubscriptionChangeResponse | null;
  currentBalance: ICreditsBalance | null;
  // The catalog's purchase mode: the preview has no quote, while a recorded refund carries its own flag.
  isSimulated: boolean;
  onConfirm: (effective: ICreditCancelEffective) => void;
  onDismiss: () => void;
}

const optionClassName = (checked: boolean): string =>
  `flex cursor-pointer gap-3 rounded-2xl border p-4 transition-colors ${
    checked ? "border-brandPurple-400 bg-brandPurple-100/40" : "border-n-300 hover:bg-n-100"
  }`;

const doneView = (
  result: ICreditSubscriptionChangeResponse | null,
  currentBalance: ICreditsBalance | null,
): DoneView => {
  const currentSubscription = currentBalance?.subscription ?? null;
  if (result === null || currentBalance === null) return {title: "Change applied", rows: []};
  if (currentSubscription?.live && currentSubscription.cancelAtPeriodEnd) {
    return {
      title: "Cancellation scheduled",
      rows: [
        {label: "Plan ends", value: formatUtc(currentSubscription.periodEnd)},
        {
          label: "Plan credits",
          value: `${formatCreditCount(currentBalance.planRemainingCredits)} stay spendable until then`,
        },
      ],
    };
  }
  if (currentSubscription?.live) return {title: "Change applied", rows: []};
  return {
    title: "Subscription ended",
    rows: [
      {
        label: "Refund recorded",
        value: result.refund
          ? `${formatCreditCount(result.refund.credits)} - ${formatAmount(result.refund.amountCents, result.refund.simulated)}`
          : "None",
      },
      {label: "Extra credits", value: `${formatCreditCount(currentBalance.nonPlanCredits)} stay`},
    ],
  };
};

const CancelSubscriptionDialog: FC<CancelSubscriptionDialogProps> = ({
  isOpen,
  subscriptionSnapshot: subscription,
  planName,
  planRemainingCredits,
  nonPlanCredits,
  phase,
  message,
  result,
  currentBalance,
  isSimulated,
  onConfirm,
  onDismiss,
}) => {
  const [effective, setEffective] = useState<ICreditCancelEffective>(DEFAULT_EFFECTIVE);

  useEffect(() => {
    if (isOpen) setEffective(DEFAULT_EFFECTIVE);
  }, [isOpen]);

  const done = phase === "done" ? doneView(result, currentBalance) : null;
  const estimate = subscription
    ? estimateUnusedRefundCents(subscription.priceCents, subscription.credits, planRemainingCredits)
    : 0;
  const options: CancelOption[] = subscription
    ? [
      {
        value: "period_end",
        label: "At period end",
        description: `Keep your ${formatCreditCount(planRemainingCredits)} until ${formatUtc(subscription.periodEnd)}. `
          + "The plan does not renew and nothing is refunded.",
      },
      {
        value: "now_with_unused_refund",
        label: "Now, with a refund of unused credits",
        description: `End the plan today. Your ${formatCount(planRemainingCredits)} unused plan credits are `
          + `refunded (${formatUsdCents(estimate)}${isSimulated ? ", simulated" : ""}); `
          + `${formatCount(nonPlanCredits)} extra credits stay.`,
      },
    ]
    : [];
  const previewRows: BillingSummaryRow[] = !subscription
    ? []
    : effective === "period_end"
      ? [
        {label: "Plan ends", value: formatUtc(subscription.periodEnd)},
        {label: "Plan credits", value: `${formatCreditCount(planRemainingCredits)} stay spendable until then`},
        {label: "Refund", value: "None"},
      ]
      : [
        {label: "Plan ends", value: "Now"},
        {label: "Unused plan credits", value: formatCreditCount(planRemainingCredits)},
        {label: "Estimated refund", value: formatAmount(estimate, isSimulated)},
        {label: "Extra credits", value: `${formatCreditCount(nonPlanCredits)} stay`},
      ];
  const intro = done || !subscription
    ? null
    : subscription.cancelAtPeriodEnd
      ? `${planName} is already set to end ${formatUtc(subscription.periodEnd)}. Choose when the cancellation takes effect.`
      : `${planName} renews ${formatUtc(subscription.periodEnd)}. Choose when the cancellation takes effect.`;

  return (
    <BillingActionConfirmation
      isOpen={isOpen}
      title={done ? done.title : "Cancel subscription"}
      intro={intro}
      rows={done ? done.rows : previewRows}
      note={isSimulated ? SIMULATED_NOTE : effective === "now_with_unused_refund" ? POLAR_REFUND_NOTE : null}
      phase={phase}
      message={message}
      confirmLabel="Confirm cancellation"
      dismissLabel="Keep subscription"
      isConfirmDisabled={subscription === null}
      onConfirm={() => onConfirm(effective)}
      onDismiss={onDismiss}
    >
      {!done && subscription && (
        <RadioGroup
          value={effective}
          onChange={setEffective}
          aria-label="When the cancellation takes effect"
          className="mt-4 flex flex-col gap-2"
          data-testid="cancel-options"
        >
          {options.map((option) => (
            <Field
              key={option.value}
              className={optionClassName(option.value === effective)}
              onClick={() => setEffective(option.value)}
            >
              <Radio
                value={option.value}
                className="group mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border border-n-400 bg-white data-[checked]:border-brandPurple-500 data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-brandPurple-400"
              >
                <span className="h-2 w-2 rounded-full bg-brandPurple-500 opacity-0 transition-opacity group-data-[checked]:opacity-100"/>
              </Radio>
              <span className="min-w-0">
                <Label as="span" className="block text-sm font-semibold text-n-700">{option.label}</Label>
                <Description as="span" className="block text-xs text-n-500 text-pretty">{option.description}</Description>
              </span>
            </Field>
          ))}
        </RadioGroup>
      )}
    </BillingActionConfirmation>
  );
};

export default CancelSubscriptionDialog;
